import { useEffect } from 'react';
import type { StorageBidGroupsResponse } from 'src/apis/storage/types';
import type { StorageOfferDetailsRequest } from 'src/apis/storage/use-storage-offer-details';
import { useAxios } from 'src/hooks/use-axios';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

type HookOptions = {
  storageOfferDetails?: StorageOfferDetailsRequest;
};

type ExecuteOptions = {
  url: string;
};

export function useOptionalStorageBids({ storageOfferDetails }: HookOptions) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteOptions) =>
      axios.request<StorageBidGroupsResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isConstraintViolation(error)) return error;
        throw error;
      },
    }
  );

  useEffect(() => {
    if (!storageOfferDetails?.response) return;
    if (!storageOfferDetails.response.data._links.getStorageBids) return;

    request.execute({
      url: storageOfferDetails.response.data._links.getStorageBids.href,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [storageOfferDetails?.response]);

  // not available on stage
  if (!storageOfferDetails) return;

  // not allowed to get bids
  if (
    storageOfferDetails.response &&
    !storageOfferDetails.response.data._links.getStorageBids
  )
    return;

  return request;
}

export type StorageBidsRequest = ReturnType<typeof useOptionalStorageBids>;
